import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  viewChild,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { mapFormItemToComponent, SupportedFormItemComponents } from '../../map-form-item-to-component';
import { extractXmlProps, formatXML, getPropsFrom, ResourceData, UserInformation } from '@lfw/roesberg.liveforms.core';
import { EditorSettings } from '../../types/editor-settings';
import { ItemMenuComponent, MenuAction } from '../../item-menu/item-menu.component';
import { CommonModule } from '@angular/common';
import {
  BASE_TEXT_COLOR_VARIANTS,
  extractColorProperties,
  getArgbToRgba,
  getTextColor
} from '../../../../utils/colors';
import { IHasInputColorControls, IHasRangeColorControls } from '../InteractiveItem/interactive-item-form.component';
import { takeUntilDestroyed, outputToObservable } from '@angular/core/rxjs-interop';

const SHARED_FIRST_COLUMN_WIDTHS_KEY = 'sharedFirstColumnWidths' as const;
const RESOURCE_DATA_CHANGED_KEY = 'resourceDataChanged' as const;
const SECTION_ITEM_NODE_NAME_INPUT_KEY = 'sectionItemNodeName' as const;
const ADD_NEW_INSTANCE_REQUIRED_KEY = 'addNewInstanceRequired' as const;
const DELETE_INSTANCE_REQUIRED_KEY = 'deleteInstanceRequired' as const;
const UPDATE_FIRST_COLUMN_WIDTH_REQUIRED_KEY = 'updateFirstColumnWidthRequired' as const;
const IS_VISIBLE_KEY = 'isVisible' as const;
const HAS_INPUT_COLOR_CONTROL_KEY = 'hasInputColorControl' as const;
const HAS_RANGE_COLOR_CONTROL_KEY = 'hasRangeColorControl' as const;
const PROPERTIES_KEY = 'properties' as const;
const INPUT_BG_COLOR_KEY = 'inputBgColor' as const;

@Component({
  selector: 'lfe-dynamic-form-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ItemMenuComponent],
  templateUrl: './dynamic-form-item.component.html',
  standalone: true,
  styleUrl: './dynamic-form-item.component.css'
})
export class DynamicFormItemComponent implements AfterViewInit, OnDestroy {
  readonly container = viewChild.required('dynamicContainer', { read: ViewContainerRef });

  readonly xml = input.required<ChildNode>();
  readonly form = input.required<FormGroup>();
  readonly user = input.required<UserInformation>();
  readonly settings = input.required<EditorSettings>();
  readonly itemIndex = input.required<number>();
  readonly isItemCreatedInCurrentSession = input.required<(itemId: string) => boolean>();
  readonly sectionItemId = input.required<string>();
  readonly sectionItemNodeName = input('');
  readonly filteredXmlItems = input.required<ChildNode[]>();
  readonly filteredXmlItemsQty = input.required<number>();
  readonly firstSiblingId = input.required<string>();
  readonly sharedFirstColumnWidthsInput = input<WritableSignal<Record<string, number>>>();
  readonly isReadonly = input(false);
  readonly debug = input(false);
  readonly orderId = input.required<string>();
  readonly isHiddenByAncestor = input(false);

  readonly addCommentRequested = output<{ itemId: string }>();
  readonly addPhotoCommentRequested = output<{ itemId: string }>();
  readonly deleteItemRequested = output<{ itemId: string }>();
  readonly resourceDataChanged = output<{ photoItemId: string; resourceData: ResourceData[] }>();
  readonly repeatInstanceAddRequested = output<{ repeatItemId: string; anchorItemId: string }>();
  readonly repeatInstanceDeleteRequested = output<{ repeatGroupSectionItemId: string }>();
  readonly updateFirstColumnWidth = output<{ id: string; width: number }>();
  readonly itemVisibilityChanged = output<{ itemId: string; isVisible: boolean }>();

  public componentRef: ComponentRef<SupportedFormItemComponents> | null = null;
  private destroyRef = inject(DestroyRef);

  showMenu = signal(false);
  isChildVisible = signal(true);
  /** Hidden either by the item's own rule or because an enclosing section is hidden. */
  isEffectivelyVisible = computed(() => this.isChildVisible() && !this.isHiddenByAncestor());
  isItemInputColorControlEnabled = signal(false);
  isItemSelectionColorControlEnabled = signal(false);
  itemSelectionColor = signal(getArgbToRgba(BASE_TEXT_COLOR_VARIANTS.white).substring(0, 7));
  currentInputBgColor = signal<string>(BASE_TEXT_COLOR_VARIANTS.white);

  itemType = computed(() => this.xml().nodeName);

  itemInputColor = computed(() => {
    const props = getPropsFrom(this.xml());
    const { inputColor } = extractColorProperties(props);
    return getArgbToRgba(inputColor).substring(0, 7);
  });

  sharedFirstColumnWidths = computed(() => this.sharedFirstColumnWidthsInput()?.());

  hasMenuHidden = computed(() => {
    const itemType = this.xml().nodeName;
    const excludedTypes = ['SectionEndItem', 'DividerItem'];
    return this.isReadonly() || excludedTypes.includes(itemType);
  });

  isCommentCreatedInCurrentSession = computed(() => {
    const itemId = this.getItemId();
    if (itemId) {
      return this.isItemCreatedInCurrentSession()(this.getItemId());
    }

    return false;
  });

  isCommentOwnedByCurrentUser = computed(() => {
    const itemId = this.getItemId();
    if (itemId) {
      const props = getPropsFrom(this.xml());
      const isCommentItem = this.itemType() === 'CommentItem';
      const isPhotoCommentItem = this.itemType() === 'PhotoItem' && props?.['AddedWhileProcessing'] === 'True';
      const isOwnComment = this.user().displayName === props?.['UserName'];
      return isOwnComment && (isCommentItem || isPhotoCommentItem);
    }

    return false;
  });

  // Effect to update component inputs reactively
  private _reactiveInputsEffect = effect(() => {
    if (
      this.sharedFirstColumnWidths() &&
      this.componentRef &&
      SHARED_FIRST_COLUMN_WIDTHS_KEY in this.componentRef.instance
    ) {
      const sharedFirstColumnWidths = this.sharedFirstColumnWidths();
      if (sharedFirstColumnWidths) {
        this.componentRef.instance.sharedFirstColumnWidths.set(sharedFirstColumnWidths);
      }
    }
  });

  private _renderedXmlItemsEffect = effect(() => {
    const items = this.filteredXmlItems();
    if (this.componentRef) {
      this.componentRef.setInput('renderedXmlItems', items);
      this.componentRef.setInput('renderedXmlItemsQty', items.length);
    }
  });

  ngAfterViewInit() {
    this.createComponent();
  }

  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  private createComponent(): void {
    const xmlNode = this.xml();
    const nodeName = xmlNode.nodeName;

    const componentClass = mapFormItemToComponent[nodeName as keyof typeof mapFormItemToComponent];

    if (!componentClass) {
      console.error('No component class found for node:', nodeName);
      return;
    }

    this.componentRef = this.container().createComponent(componentClass);
    this.componentRef.setInput('xml', xmlNode);
    this.componentRef.setInput('form', this.form());
    this.componentRef.setInput('isReadonly', this.isReadonly());
    this.componentRef.setInput('user', this.user());
    this.componentRef.setInput('settings', this.settings());
    this.componentRef.setInput('sectionItemId', this.sectionItemId());
    this.componentRef.setInput('firstSiblingItemId', this.firstSiblingId());
    this.componentRef.setInput('renderedXmlItems', this.filteredXmlItems());
    this.componentRef.setInput('renderedXmlItemsQty', this.filteredXmlItemsQty());

    if (componentClass === mapFormItemToComponent['PhotoItem']) {
      this.componentRef.setInput('orderId', this.orderId());
    }

    if (SECTION_ITEM_NODE_NAME_INPUT_KEY in this.componentRef.instance) {
      this.componentRef.setInput(SECTION_ITEM_NODE_NAME_INPUT_KEY, this.sectionItemNodeName());
    }

    // Set initial sharedFirstColumnWidths if available
    if (SHARED_FIRST_COLUMN_WIDTHS_KEY in this.componentRef.instance && this.sharedFirstColumnWidths) {
      const sharedFirstColumnWidths = this.sharedFirstColumnWidths();
      if (sharedFirstColumnWidths) {
        this.componentRef.instance.sharedFirstColumnWidths.set(sharedFirstColumnWidths);
      }
    }

    // Subscribe to visibility changes from the child component
    if (this.componentRef.instance.visibilityChanged) {
      outputToObservable(this.componentRef.instance.visibilityChanged)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((isVisible: boolean) => {
          this.isChildVisible.set(isVisible);
          this.itemVisibilityChanged.emit({ itemId: this.getItemId(), isVisible });
        });
    }

    // Subscribe to resource data changes from PhotoItem components
    if (RESOURCE_DATA_CHANGED_KEY in this.componentRef.instance) {
      this.componentRef.instance.resourceDataChanged.subscribe(
        (event: { photoItemId: string; resourceData: ResourceData[] }) => {
          this.resourceDataChanged.emit(event);
        }
      );
    }

    // Subscribe to RepeatItem new instance required events
    if (ADD_NEW_INSTANCE_REQUIRED_KEY in this.componentRef.instance) {
      this.componentRef.instance.addNewInstanceRequired.subscribe(
        ({ repeatItemId, anchorItemId }: { repeatItemId: string; anchorItemId: string }) => {
          this.repeatInstanceAddRequested.emit({
            repeatItemId,
            anchorItemId
          });
        }
      );
    }

    // Subscribe to RepeatItem delete instance required events
    if (DELETE_INSTANCE_REQUIRED_KEY in this.componentRef.instance) {
      this.componentRef.instance.deleteInstanceRequired.subscribe(
        ({ repeatGroupSectionItemId }: { repeatGroupSectionItemId: string }) => {
          this.repeatInstanceDeleteRequested.emit({
            repeatGroupSectionItemId
          });
        }
      );
    }

    // Subscribe to update first column width required events
    if (UPDATE_FIRST_COLUMN_WIDTH_REQUIRED_KEY in this.componentRef.instance) {
      outputToObservable(this.componentRef.instance.updateFirstColumnWidthRequired)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(({ id, width }: { id: string; width: number }) => {
          this.updateFirstColumnWidth.emit({ id, width });
        });
    }

    // Set initial visibility state
    if (IS_VISIBLE_KEY in this.componentRef.instance) {
      const isVisible = this.componentRef.instance.isVisible();
      this.isChildVisible.set(isVisible);
      if (!isVisible) {
        // A section whose rule is already false emits nothing, so report it once or its contents
        // would stay visible until the rule next changes.
        this.itemVisibilityChanged.emit({ itemId: this.getItemId(), isVisible });
      }
    }

    if (HAS_INPUT_COLOR_CONTROL_KEY in this.componentRef.instance) {
      this.isItemInputColorControlEnabled.set(this.componentRef.instance.hasInputColorControl);
    }

    if (HAS_RANGE_COLOR_CONTROL_KEY in this.componentRef.instance) {
      this.isItemSelectionColorControlEnabled.set(this.componentRef.instance.hasRangeColorControl);
    }

    if (PROPERTIES_KEY in this.componentRef.instance && INPUT_BG_COLOR_KEY in this.componentRef.instance.properties) {
      const inputBgColor = this.componentRef.instance.properties.inputBgColor as string;
      this.currentInputBgColor.set(inputBgColor || BASE_TEXT_COLOR_VARIANTS.white);
    }
  }

  toggleMenu(): void {
    if (this.showMenu()) return this.closeMenu();
    return this.openMenu();
  }

  openMenu(): void {
    this.showMenu.set(true);
  }

  closeMenu(): void {
    this.showMenu.set(false);
  }

  hasComponentRefInputColor = computed(
    () => this.componentRef && HAS_INPUT_COLOR_CONTROL_KEY in this.componentRef.instance
  );
  hasComponentRefRangeColor = computed(
    () => this.componentRef && HAS_RANGE_COLOR_CONTROL_KEY in this.componentRef.instance
  );

  onMenuAction(action: MenuAction): void {
    switch (action.action) {
      case 'addComment':
        this.addCommentRequested.emit({ itemId: this.getItemId() });
        break;
      case 'addPhotoComment':
        this.addPhotoCommentRequested.emit({ itemId: this.getItemId() });
        break;
      case 'deleteItem':
        this.deleteItemRequested.emit({ itemId: this.getItemId() });
        break;
      case 'getXml':
        this.handleGetXml();
        break;
      case 'getFormattedXml':
        this.handleGetXml(true);
        break;
      case 'setInputColor':
        this.setInputColor(String(action.data));
        if (String(action.data) && !this.form().dirty) {
          this.form().markAsDirty();
        }
        break;
      case 'removeInputColor':
        this.removeInputColor();
        this.form().markAsDirty();
        break;

      case 'setSelectionColor':
        this.setSelectionColor(String(action.data));
        if (String(action.data) && !this.form().dirty) {
          this.form().markAsDirty();
        }
        break;
      case 'removeSelectionColor':
        this.removeSelectionColor();
        this.form().markAsDirty();
        break;
    }
  }

  private removeInputColor(): void {
    if (this.hasComponentRefInputColor()) {
      const instance = this.componentRef?.instance as IHasInputColorControls;
      instance.currentInputBgColor.set(BASE_TEXT_COLOR_VARIANTS.white);
      instance.currentInputTextColor.set(getTextColor(''));
    }
  }

  private setInputColor(color: string): void {
    if (this.hasComponentRefInputColor()) {
      const instance = this.componentRef?.instance as IHasInputColorControls;
      instance.currentInputBgColor.set(color);
      instance.currentInputTextColor.set(getTextColor(color));
    }
  }

  private setSelectionColor(color: string): void {
    if (this.hasComponentRefRangeColor()) {
      const instance = this.componentRef?.instance as IHasRangeColorControls;
      instance.setRangeBackgroundColor(color);
    }
  }

  private removeSelectionColor(): void {
    if (this.hasComponentRefRangeColor()) {
      const instance = this.componentRef?.instance as IHasRangeColorControls;
      instance.setRangeBackgroundColor('');
    }
  }

  private handleGetXml(formatted = false): void {
    if (this.componentRef && this.componentRef.instance.getXML) {
      const xml = this.componentRef.instance.getXML();
      console.log(formatted ? formatXML(xml) : xml);
    } else {
      console.warn('getXML method not available for this item');
    }
  }

  private getItemId(): string {
    // Get the ID from the XML child elements (CustomID first, then ID)
    const element = this.xml() as Element;

    const props = extractXmlProps(element);

    if (props['CustomID'] || props['ID']) {
      return props['CustomID'] || props['ID'];
    }

    console.warn('No ID found for element:', element.nodeName);
    return '';
  }
}
